// src/pages/articles/articles.component.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Keep this import
import { firestore, collection, getDocs, query, orderBy } from '../../firebase/firebase.utils';
import './articles.styles.scss';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    { id: 'all', label: 'All Posts', icon: '📚' },
    { id: 'Hair Wigs', label: 'Hair Wigs', icon: '💇' },
    { id: 'Hair Patch', label: 'Hair Patch', icon: '🪄' },
    { id: 'Hair Extensions', label: 'Hair Extensions', icon: '💁' },
    { id: 'Hair Replacement', label: 'Hair Replacement', icon: '🔄' },
    { id: 'Hair Care', label: 'Hair Care', icon: '🧴' },
  ];

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const articlesRef = collection(firestore, 'articles');
        const q = query(articlesRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const articlesList = [];
        querySnapshot.forEach((doc) => {
          articlesList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setArticles(articlesList);
        if (articlesList.length > 0) {
          setFeaturedArticle(articlesList[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading articles:', error);
        setLoading(false);
      }
    };
    
    loadArticles();
  }, []);

  // ADD THIS: Check URL on load to open correct article
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/article/')) {
      const slug = path.replace('/article/', '');
      const article = articles.find(a => a.slug === slug);
      if (article) {
        setSelectedArticle(article);
      }
    }
  }, [articles]);

  const filteredArticles = activeCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const copyArticleUrl = (slug, e) => {
    e.preventDefault();
    e.stopPropagation();
    const articleUrl = `${window.location.origin}/article/${slug}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(articleUrl)
        .then(() => {
          alert('✅ Article URL copied to clipboard!\n\n' + articleUrl);
        })
        .catch(() => {
          prompt('Copy this URL:', articleUrl);
        });
    } else {
      prompt('Copy this URL:', articleUrl);
    }
  };

  // UPDATED: Toggle article and change URL
  const toggleArticle = (article) => {
    if (selectedArticle && selectedArticle.id === article.id) {
      setSelectedArticle(null);
      // Change URL back to /articles
      window.history.pushState({}, '', '/articles');
    } else {
      setSelectedArticle(article);
      // Change URL to /article/slug
      window.history.pushState({}, '', `/article/${article.slug}`);
    }
  };

  if (loading) {
    return (
      <div className="articles-loading">
        <div className="loader"></div>
        <p>Loading articles...</p>
      </div>
    );
  }

  return (
    <div className="articles-page">
      {/* ===== HERO SECTION ===== */}
      <section className="articles-hero">
        <div className="hero-content">
          <h1>📖 Hair Care Blog</h1>
          <p>Your ultimate guide to hair wigs, patches, extensions & replacement in Pakistan</p>
          <div className="hero-stats">
            <div className="stat">
              <span className="number">{articles.length}</span>
              <span className="label">Articles</span>
            </div>
            <div className="stat">
              <span className="number">50+</span>
              <span className="label">Topics</span>
            </div>
            <div className="stat">
              <span className="number">4.9⭐</span>
              <span className="label">Reader Rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURED ARTICLE ===== */}
      {featuredArticle && (
        <section className="featured-article">
          <div className="featured-badge">⭐ Featured</div>
          <div className="featured-grid">
            <div className="featured-image">
              <img 
                src={featuredArticle.image || '/images/default-article.jpg'} 
                alt={featuredArticle.title}
                onError={(e) => {
                  e.target.src = '/images/default-article.jpg';
                }}
              />
              <span className="featured-category">{featuredArticle.category}</span>
            </div>
            <div className="featured-content">
              <h2>{featuredArticle.title}</h2>
              <p className="featured-excerpt">{featuredArticle.excerpt}</p>
              <div className="featured-meta">
                <span>📅 {formatDate(featuredArticle.date || featuredArticle.createdAt)}</span>
                <span>⏱️ {featuredArticle.readTime || '3 min read'}</span>
                <button
                  onClick={(e) => copyArticleUrl(featuredArticle.slug, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0066cc',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '0',
                    marginLeft: '10px',
                    textDecoration: 'underline'
                  }}
                  title="Copy article URL"
                >
                  📋 Copy URL
                </button>
              </div>
              <button 
                onClick={() => toggleArticle(featuredArticle)}
                className="featured-read-btn"
                style={{ cursor: 'pointer' }}
              >
                {selectedArticle && selectedArticle.id === featuredArticle.id ? 'Close Article' : 'Read Full Article →'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ===== CATEGORIES ===== */}
      <section className="categories-section">
        <div className="categories-scroll">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              <span className="category-icon">{category.icon}</span>
              {category.label}
              {category.id !== 'all' && (
                <span className="category-count">
                  {articles.filter(a => a.category === category.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* ===== ARTICLES GRID ===== */}
      <section className="articles-grid-section">
        {filteredArticles.length === 0 ? (
          <div className="no-articles">
            <span>📝</span>
            <h3>No Articles Found</h3>
            <p>Check back soon for new articles on hair care in Pakistan.</p>
          </div>
        ) : (
          <div className="articles-grid">
            {filteredArticles.map((article, index) => (
              <div className={`article-card ${index === 0 ? 'highlight' : ''}`} key={article.id}>
                <div className="article-image-wrapper">
                  <img 
                    src={article.image || '/images/default-article.jpg'} 
                    alt={article.title}
                    onError={(e) => {
                      e.target.src = '/images/default-article.jpg';
                    }}
                  />
                  {article.category && (
                    <span className="article-category-tag">{article.category}</span>
                  )}
                  {article.isNew && <span className="new-tag">New</span>}
                </div>
                <div className="article-body">
                  <h3>
                    <span style={{ cursor: 'pointer' }} onClick={() => toggleArticle(article)}>
                      {article.title}
                    </span>
                  </h3>
                  <p className="article-excerpt">{article.excerpt}</p>
                  
                  {/* SHOW FULL ARTICLE CONTENT WHEN SELECTED */}
                  {selectedArticle && selectedArticle.id === article.id && (
                    <div className="full-article-content" style={{
                      marginTop: '20px',
                      padding: '20px',
                      background: '#f9f9f9',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      {article.image && (
                        <img 
                          src={article.image} 
                          alt={article.title}
                          style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px', marginBottom: '15px' }}
                        />
                      )}
                      <div dangerouslySetInnerHTML={{ __html: article.content }} />
                      <button 
                        onClick={() => toggleArticle(article)}
                        style={{
                          marginTop: '15px',
                          padding: '8px 20px',
                          background: '#0066cc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Close Article
                      </button>
                    </div>
                  )}
                  
                  <div className="article-footer">
                    <div className="article-meta">
                      <span>📅 {formatDate(article.date || article.createdAt)}</span>
                      <span>⏱️ {article.readTime || '3 min read'}</span>
                      <button
                        onClick={(e) => copyArticleUrl(article.slug, e)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0066cc',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '0',
                          marginLeft: '5px',
                          textDecoration: 'underline'
                        }}
                        title="Copy article URL"
                      >
                        📋 Copy URL
                      </button>
                    </div>
                    <button 
                      onClick={() => toggleArticle(article)}
                      className="read-more-btn"
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedArticle && selectedArticle.id === article.id ? 'Close Article' : 'Read More →'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ===== NEWSLETTER SECTION ===== */}
      <section className="newsletter-section">
        <div className="newsletter-content">
          <h2>📧 Get Hair Care Tips</h2>
          <p>Subscribe to get the latest articles on hair wigs, patches and replacement in Pakistan</p>
          <form className="newsletter-form">
            <input type="email" placeholder="Enter your email" required />
            <button type="submit">Subscribe</button>
          </form>
        </div>
      </section>

      {/* ===== SEO SCHEMA ===== */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Crispy Hair Solution Articles",
          "description": "Expert articles on hair wigs, patches, extensions and hair replacement in Pakistan",
          "url": "https://crispyhairsolution.com/articles",
          "blogPost": articles.map(article => ({
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.excerpt,
            "datePublished": article.date || new Date(article.createdAt).toISOString().split('T')[0],
            "url": `${window.location.origin}/article/${article.slug}`,
            "author": {
              "@type": "Organization",
              "name": "Crispy Hair Solution"
            }
          }))
        })}
      </script>
    </div>
  );
};

export default Articles;